import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import React, { Component } from 'react';

import config from '@/config';
import { hasAttemptedChunkReload, isChunkLoadError } from '@/utils/chunk-load-error';
import { isTranslateDomError } from '@/utils/translate-dom-guard';

// Get app version from environment variable (injected at build time via next.config.js)
// This works on both client and server side
const appVersion = process.env.NEXT_PUBLIC_APP_VERSION || 'unknown';

// Get Bugsnag API key - server uses BUGSNAG_SERVER_API_KEY, client uses NEXT_PUBLIC_BUGSNAG_BROWSER_API_KEY
// NEXT_PUBLIC_* variables are embedded at build time and only available on the client
// BUGSNAG_SERVER_API_KEY is only available on the server
const isServer = typeof window === 'undefined';
const apiKey = isServer
  ? process.env.BUGSNAG_SERVER_API_KEY
  : process.env.NEXT_PUBLIC_BUGSNAG_BROWSER_API_KEY;

class DevErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.log(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const { FallbackComponent } = this.props;
      if (FallbackComponent) {
        return <FallbackComponent error={this.state.error} />;
      }
      return <h1>Something went wrong.</h1>;
    }

    return this.props.children;
  }
}

let ErrBoundary = DevErrorBoundary;

// Don't log these errors
const errorsToIgnore = [
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications.',
];

let client = {
  notify: (e, ...rest) => {
    console.log('NOTIFY', e, rest);
  },
  leaveBreadcrumb: (e, ...rest) => {
    console.log('LEAVE BREADCRUMB', e, rest);
  },
  setUser(...rest) {
    console.log('SET USER', rest);
  },
};

if (apiKey && process.env.CI !== 'true') {
  const options = {
    appVersion,
    releaseStage: config.commerceEnv,
    apiKey,
  };

  Bugsnag.start({
    ...options,
    plugins: [new BugsnagPluginReact(React)],
    onError: (event) => {
      const err = event.errors[0];
      const msg = err.errorMessage;

      if (errorsToIgnore.includes(msg)) {
        return false;
      }

      // Browser translate / extensions mutate the DOM; React then throws
      // NotFoundError on insertBefore/removeChild. The client guard swallows
      // the crash; ignore residual reports from older bundles.
      if (isTranslateDomError({ name: err.errorClass, message: msg })) {
        return false;
      }

      // Suppress one-off stale-tab chunk failures that will auto-reload.
      // Still report when the reload guard already tripped (stuck session).
      const chunkError = { name: err.errorClass, message: msg };
      if (isChunkLoadError(chunkError) && !hasAttemptedChunkReload()) {
        return false;
      }

      return true;
    },
  });

  ErrBoundary = Bugsnag.getPlugin('react');
  client = Bugsnag;
}

export default client;
export const ErrorBoundary = ErrBoundary;
